// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R
import { GetResourceHistoryList, getResourceHistoryList } from 'apis/resource';
import TableLoading from 'components/atoms/TableLoading';
import DOMPurify from 'dompurify';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getSlaFormatYearMonthDate } from 'ts/formatDate';
import { DEFAULT_PAGE, PAGE_SIZE } from 'util/ConstantValues';
import {
  ResourceEditAvailableFields,
  ResourceEditAvailableFieldsName,
  ResourceEmissionsEditFieldName,
  ResourceHistoryItem,
  ResourseHistoryStatus,
} from 'util/adminResourceManagementTypes';
import styles from './AdminResourceHistoryTable.module.scss';
import { CustomCsvDownloadButton } from 'components/atoms/CustomCsvDownloadButton';
import Csv from 'util/csv';
import TableFilterCalendar from 'components/molecules/TableFilterCalendar';
import IconCalendarTablet from 'components/atoms/icons/IconCalendarTablet';

const AdminResourceHistoryTable = () => {
  const { t } = useTranslation();
  const [totalItems, setTotalItems] = useState(0);
  const [listResourceHistoies, setListResourceHistoies] = useState<
    ResourceHistoryItem[]
  >([]);
  const [currentPage, setCurrentPage] = useState(DEFAULT_PAGE);
  const [dateFrom, setDateFrom] = useState<string | null>(null);
  const [dateTo, setDateTo] = useState<string | null>(null);

  const tableRef = useRef<HTMLTableElement>(null);

  const fetchResourceHistory = async () => {
    getResourceHistoryList(
      {
        page: currentPage || DEFAULT_PAGE,
        take: PAGE_SIZE,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
      },
      (data) => {
        if (data) {
          if (currentPage && currentPage > DEFAULT_PAGE) {
            setListResourceHistoies((prev) => [
              ...prev,
              ...(data.resourceHistories || []),
            ]);
          } else {
            setListResourceHistoies(data.resourceHistories || []);
          }
          setTotalItems(data?.total || 0);
        }
      }
    );
  };

  useEffect(() => {
    fetchResourceHistory();
  }, [currentPage, dateFrom, dateTo]);

  const getListFieldsEdited = (item: ResourceHistoryItem) => {
    return Object.keys(
      item.isDeleted
        ? {
            materialName: item.oldData.emission?.materialName,
            wasteUnitName: item.oldData.emission?.description,
            ...item.oldData,
          }
        : item.newData
    );
  };

  const getFieldDisplayName = (
    field: string,
    target: ResourseHistoryStatus
  ): string | undefined => {
    return target !== ResourseHistoryStatus.Emissions
      ? ResourceEditAvailableFieldsName[field]
      : ResourceEmissionsEditFieldName[field];
  };

  const getFieldValue = (
    data: ResourceEditAvailableFields,
    field: string
  ): string => {
    if (field === 'emissionDatetime') {
      return getSlaFormatYearMonthDate(data[field] || '');
    }
    if (field === 'qualityInspectionMethod') {
      return data[field]?.join(', ') || '';
    }
    if (
      field === 'wasteUnitName' &&
      !data[field] &&
      data?.emission?.description
    ) {
      return data?.emission?.description || '-';
    }
    return data[field] || data?.emission?.[field] || '-';
  };

  const csvHeaders: (string | [string, string])[] = useMemo(() => {
    return [
      t('resource_list_screen.thead_edit_time'),
      t('resource_list_screen.thead_editor_user_id'),
      t('resource_list_screen.thead_edited_resource_id'),
      t('resource_list_screen.thead_action'),
      t('resource_list_screen.thead_edited_fields'),
      t('resource_list_screen.thead_old_data'),
      t('resource_list_screen.thead_new_data'),
    ] as string[];
  }, [t]);

  const handleDownloadCsv = () => {
    getResourceHistoryList(
      { page: 0, dateFrom: dateFrom || undefined, dateTo: dateTo || undefined },
      (data) => {
        if (data) {
          new Csv({
            headers: csvHeaders,
            data:
              (data as GetResourceHistoryList).resourceHistories.map((item) => [
                getSlaFormatYearMonthDate(item.registDatetime),
                item.userId,
                item.targetId,
                t(
                  `resource_list_screen.action_${
                    item.isDeleted ? 'delete' : 'edit'
                  }`
                ),
                getListFieldsEdited(item)
                  .map((field) => getFieldDisplayName(field, item.target) || '')
                  .filter(Boolean)
                  .join('\n'),
                getListFieldsEdited(item)
                  .filter((field) => !!ResourceEditAvailableFieldsName[field])
                  .map((field) => getFieldValue(item.oldData, field))
                  .join('\n'),
                getListFieldsEdited(item)
                  .filter((field) => !!ResourceEditAvailableFieldsName[field])
                  .map((field) =>
                    !item.isDeleted ? getFieldValue(item.newData, field) : ''
                  )
                  .join('\n'),
              ]) || [],
          }).downLoadCsv({
            filename: t('resource_list_screen.title'),
          });
        }
      }
    );
  };

  return (
    <div className={styles.tableList}>
      <div className={styles.titleTable}>
        <div
          className={styles.countList}
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(
              t('common.page', {
                page: totalItems,
              }) ?? ''
            ),
          }}
        />
      </div>
      <div className="search-area">
        <div className={styles.calendarField}>
          <TableFilterCalendar
            expanded={{
              placeholder: t('common.placeholder.select_date_range'),
              icon: <IconCalendarTablet />,
            }}
            containerClassName={styles.datePickerContainer}
            portalPositionPoint={(ref) => ref}
            darkMode={false}
            fullWidth
            value={[dateFrom || '', dateTo || '']}
            onChange={(value) => {
              const newDateFrom = value?.[0] || null;
              const newDateTo = value?.[1] || null;
              setDateFrom(newDateFrom);
              setDateTo(newDateTo);
              setCurrentPage(DEFAULT_PAGE);
              tableRef.current?.scrollTo({
                top: 0,
                behavior: 'instant' as ScrollBehavior,
              });
            }}
          />
        </div>
        <CustomCsvDownloadButton onClick={handleDownloadCsv} />
      </div>
      <div className={`scroll-wrapper ${styles.scrollWrapper}`}>
        <table id={styles.resourceHistoryTable} ref={tableRef}>
          <thead>
            <tr>
              <th className={styles.editTime}>
                {t('resource_list_screen.thead_edit_time')}
              </th>
              <th>{t('resource_list_screen.thead_editor_user_id')}</th>
              <th>{t('resource_list_screen.thead_edited_resource_id')}</th>
              <th>{t('resource_list_screen.thead_action')}</th>
              <th>{t('resource_list_screen.thead_edited_fields')}</th>
              <th>{t('resource_list_screen.thead_old_data')}</th>
              <th>{t('resource_list_screen.thead_new_data')}</th>
            </tr>
          </thead>
          <tbody>
            {listResourceHistoies && listResourceHistoies.length > 0 ? (
              listResourceHistoies.map((item: ResourceHistoryItem) => {
                return (
                  <tr key={item.id}>
                    <td className={styles.editTime}>
                      <p>{getSlaFormatYearMonthDate(item.registDatetime)}</p>
                    </td>
                    <td>
                      <p>{item.userId || '-'}</p>
                    </td>
                    <td>
                      <p>{item.targetId || '-'}</p>
                    </td>
                    <td>
                      <p>
                        {t(
                          `resource_list_screen.action_${
                            item.isDeleted ? 'delete' : 'edit'
                          }`
                        )}
                      </p>
                    </td>
                    <td>
                      {getListFieldsEdited(item).map((field) => {
                        const displayName = getFieldDisplayName(
                          field,
                          item.target
                        );
                        return displayName && <p key={field}>{displayName}</p>;
                      })}
                    </td>
                    <td>
                      {getListFieldsEdited(item).map(
                        (field) =>
                          ResourceEditAvailableFieldsName[field] && (
                            <p key={field}>
                              {getFieldValue(item.oldData, field)}
                            </p>
                          )
                      )}
                    </td>
                    <td>
                      {!item.isDeleted &&
                        getListFieldsEdited(item).map(
                          (field) =>
                            ResourceEditAvailableFieldsName[field] && (
                              <p key={field}>
                                {getFieldValue(item.newData, field)}
                              </p>
                            )
                        )}
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td className="msg-error">{t('messages.M_008')}</td>
              </tr>
            )}
          </tbody>
          {listResourceHistoies && listResourceHistoies.length > 0 && (
            <TableLoading
              onNext={() => {
                if (
                  listResourceHistoies.length >= PAGE_SIZE &&
                  listResourceHistoies.length < totalItems
                ) {
                  setCurrentPage((prev) => prev + 1);
                }
              }}
            />
          )}
        </table>
      </div>
    </div>
  );
};

export default AdminResourceHistoryTable;
